import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Descriptions, Typography } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import { useGenerateSasUrlMutation } from '@/store/apis/documentsApi';
import { TaxDocumentRecord } from '@/store/apis/taxDocumentsApi';
import { DocumentViewer } from '@/components/DocumentViewer';
import {
  DOC_TYPE_LABELS,
  SUB_TYPE_LABELS,
} from '../constants/tax-documents.enum';
import DataViewer from './DataViewer';

const { Text } = Typography;

const getFileName = (doc: TaxDocumentRecord): string =>
  doc.copied?.newName ?? doc.drive?.name ?? '';

interface Props {
  doc: TaxDocumentRecord;
  onClose: () => void;
}

/**
 * A tax document with everything the pipeline pulled out of it underneath.
 *
 * The shell - viewer, download, collapsible pane - is the shared
 * `DocumentViewer`; what is left here is the tax-specific half: where the URL
 * comes from, and what goes in the pane.
 */
const TaxPreviewPanel: React.FC<Props> = ({ doc, onClose }) => {
  const [sasUrl, setSasUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  /* A read URL outlives a click, so stepping back to a document already opened
   * in this panel should not cost another mint. */
  const urlCache = useRef<Map<string, string>>(new Map());

  const [generateSasUrl] = useGenerateSasUrlMutation();

  const fileName = getFileName(doc);
  const container = doc.blob?.container;
  const blobName = doc.blob?.blobName;

  useEffect(() => {
    if (!container || !blobName) {
      setSasUrl(null);
      return;
    }

    const cacheKey = `${container}/${blobName}`;
    const cached = urlCache.current.get(cacheKey);
    if (cached) {
      setSasUrl(cached);
      return;
    }

    let current = true;
    setSasUrl(null);
    setIsLoading(true);
    generateSasUrl({ containerName: container, blobName })
      .unwrap()
      .then((res) => {
        const url = res?.data?.url ?? null;
        if (url) urlCache.current.set(cacheKey, url);
        if (current) setSasUrl(url);
      })
      .catch(() => {
        if (current) setSasUrl(null);
      })
      .finally(() => {
        if (current) setIsLoading(false);
      });

    return () => {
      current = false;
    };
  }, [container, blobName, generateSasUrl]);

  const hasData = Boolean(doc.data || doc.extras);
  const hasMetadata = Boolean(
    doc.financialYearEnd || doc.startDate || doc.issueDate || doc.identifiers,
  );

  const detail = useMemo(() => {
    if (!hasData && !hasMetadata) return undefined;

    return {
      label: 'Extracted Data',
      icon: <TableOutlined style={{ color: '#1677ff' }} />,
      note: doc.docType
        ? `· ${DOC_TYPE_LABELS[doc.docType] ?? doc.docType}${
            doc.docSubType
              ? ` · ${SUB_TYPE_LABELS[doc.docSubType] ?? doc.docSubType}`
              : ''
          }`
        : undefined,
      content: (
        <>
          {hasMetadata && (
            <Descriptions
              size="small"
              column={{ xs: 2, sm: 3 }}
              style={{ marginBottom: 12 }}
            >
              {doc.financialYearEnd && (
                <Descriptions.Item label="FY End">
                  <Text strong>{doc.financialYearEnd}</Text>
                </Descriptions.Item>
              )}
              {doc.startDate && (
                <Descriptions.Item label="Period">
                  <Text strong>
                    {doc.startDate} → {doc.endDate}
                  </Text>
                </Descriptions.Item>
              )}
              {doc.issueDate && (
                <Descriptions.Item label="Issue Date">
                  <Text strong>{doc.issueDate}</Text>
                </Descriptions.Item>
              )}
              {doc.identifiers?.bank_name && (
                <Descriptions.Item label="Bank">
                  <Text strong>{doc.identifiers.bank_name}</Text>
                </Descriptions.Item>
              )}
              {doc.identifiers?.account_last4 && (
                <Descriptions.Item label="Account">
                  <Text strong>···{doc.identifiers.account_last4}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
          <DataViewer data={doc.data} extras={doc.extras} />
        </>
      ),
    };
  }, [doc, hasData, hasMetadata]);

  return (
    <DocumentViewer
      url={sasUrl}
      isLoading={isLoading}
      fileName={fileName || 'Document Preview'}
      contentType={doc.blob?.contentType}
      onClose={onClose}
      detail={detail}
      emptyDescription="Unable to load this document."
    />
  );
};

export default TaxPreviewPanel;
